import React from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { useMailboxGuide } from '../hooks/useMailboxGuide';
import { AdminMessage } from './AdminMessage';
import { CheckProgress } from './CheckProgress';
import { Connected } from './Connected';
import { GuideFooter } from './GuideFooter';
import { MailboxErrorBox } from './MailboxErrorBox';
import { ProviderGuide } from './ProviderGuide';
import { StepWhichEmail } from './StepWhichEmail';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';

interface MailboxGuideModalProps {
  /** The artist of the setup (the new mailbox is for this artist); null: the whole workspace. */
  accountId: number | null;
  /** The campaign whose sample pitch "Send me a test" sends; null: a fixed sample. */
  campaignUuid?: string | null;
  onClose: () => void;
  onConnected?: (mailboxUuid: string) => void;
  /** A connected mailbox: its address and name start filled ("Change the password", design j3). */
  initial?: { email: string; displayName: string };
}

/**
 * "Connect your mailbox" (design outreach-v2 o, p): which email (p1, p2, p4), the guide of the provider
 * (o2 to o5), the check and its plain errors (o6), connected with a test pitch (o7). The setup step
 * 4 and Settings → Outreach open it. Any workspace member may connect.
 */
const MailboxGuideModalComponent: React.FC<MailboxGuideModalProps> = ({
  accountId,
  campaignUuid = null,
  onClose,
  onConnected,
  initial,
}) => {
  const intl = useIntl();
  const guide = useMailboxGuide(accountId, onConnected, initial);
  const { phase, providerId } = guide;

  const content = () => {
    if (phase === 'connected' && guide.mailboxUuid !== null) {
      return (
        <Connected
          accountId={accountId}
          mailboxUuid={guide.mailboxUuid}
          address={guide.email.trim()}
          campaignUuid={campaignUuid}
        />
      );
    }
    if (phase === 'checking') {
      return <CheckProgress />;
    }
    if (phase === 'admin' && providerId !== null) {
      return <AdminMessage email={guide.email} providerId={providerId} />;
    }
    if (phase === 'guide' && providerId !== null) {
      return (
        <ProviderGuide
          providerId={providerId}
          password={guide.password}
          onPassword={guide.setPassword}
          knownServers={guide.knownServers}
          manualServers={guide.manualServers}
          onManualServers={guide.setManualServers}
          onUseAnother={guide.pickAnother}
          onAdmin={() => guide.setPhase('admin')}
          error={
            guide.error === null ? undefined : (
              <MailboxErrorBox
                error={guide.error}
                providerId={providerId}
                imapHost={guide.servers.imapHost}
                onShowHow={() => guide.setPhase('guide')}
                onEditServers={() => guide.pick('other')}
                onAdmin={() => guide.setPhase('admin')}
                onUseAnother={guide.pickAnother}
              />
            )
          }
        />
      );
    }
    return (
      <StepWhichEmail
        email={guide.email}
        displayName={guide.displayName}
        onEmail={guide.setEmail}
        onDisplayName={guide.setDisplayName}
        detectedId={guide.detectedId}
        isDetecting={guide.isDetecting}
        providerId={providerId}
        onPick={guide.pick}
        onDismissDetection={guide.dismissDetection}
        onStartOver={guide.startOver}
      />
    );
  };

  return (
    <Modal isOpen toggle={onClose} centered size="lg" aria-label={intl.formatMessage(messages.dialogLabel)}>
      <ModalBody className="d-flex flex-column gap-3 p-4" data-testid="mailbox-guide">
        {content()}
      </ModalBody>
      <GuideFooter guide={guide} onClose={onClose} />
    </Modal>
  );
};

export const MailboxGuideModal = TypedMemo(MailboxGuideModalComponent);
